import { Box, styled } from "@material-ui/core";
import { useState } from "react";
import {
  EPOCUS_ICON_ACTIVE,
  EPOCUS_INACTIVE,
  EPOCUS_PRIMARY,
} from "./epocus-palette";

// ─── Styled ───────────────────────────────────────────────────────────────────

const StarRow = styled(Box)({
  display: "flex",
  gap: 4,
  alignItems: "center",
});

// 44×44pt touch target per WCAG / bedside-use requirement
const StarButton = styled("button")({
  width: 44,
  height: 44,
  padding: 0,
  background: "none",
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 8,
  "&:focus-visible": {
    outline: `2px solid ${EPOCUS_PRIMARY}`,
    outlineOffset: 2,
  },
  "&:disabled": {
    cursor: "default",
  },
});

// ─── SVG Star ─────────────────────────────────────────────────────────────────

const STAR_PATH =
  "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z";

const StarSvg: FC<{
  fill: "full" | "half" | "empty";
  active: boolean;
  clipId: string;
}> = ({ fill, active, clipId }) => {
  const filledColor = active ? EPOCUS_PRIMARY : EPOCUS_ICON_ACTIVE;

  return (
    <svg width={28} height={28} viewBox="0 0 24 24" aria-hidden="true">
      {fill === "half" && (
        <defs>
          <clipPath id={clipId}>
            <rect x="0" y="0" width="12" height="24" />
          </clipPath>
        </defs>
      )}
      {/* Empty star background */}
      <path d={STAR_PATH} fill={EPOCUS_INACTIVE} opacity={0.25} />
      {/* Filled portion */}
      {fill !== "empty" && (
        <path
          d={STAR_PATH}
          fill={filledColor}
          clipPath={fill === "half" ? `url(#${clipId})` : undefined}
        />
      )}
    </svg>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

export const EpocusStarRating: FC<{
  value: number;
  onChange?: (value: number) => void;
  readOnly?: boolean;
  wholeStarsOnly?: boolean;
}> = ({ value, onChange, readOnly = false, wholeStarsOnly = false }) => {
  const [hoverValue, setHoverValue] = useState<number | null>(null);

  const displayValue = hoverValue ?? value;

  const getFill = (star: number): "full" | "half" | "empty" => {
    if (displayValue >= star) return "full";
    if (displayValue >= star - 0.5) return "half";
    return "empty";
  };

  const handleStarInteract = (
    star: number,
    clientX: number,
    clientY: number,
    rect: DOMRect,
    mode: "click" | "move",
  ) => {
    // Keyboard activation produces clientX === 0 && clientY === 0 — treat as full star
    const isKeyboard = clientX === 0 && clientY === 0;
    const isLeft =
      !wholeStarsOnly && !isKeyboard && clientX - rect.left < rect.width / 2;
    const next = isLeft ? star - 0.5 : star;
    if (mode === "click") {
      onChange?.(next);
    } else {
      setHoverValue(next);
    }
  };

  return (
    <StarRow
      role="radiogroup"
      aria-label="Image quality score"
      onMouseLeave={() => setHoverValue(null)}
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const fill = getFill(star);
        const isActive = hoverValue !== null && hoverValue >= star - 0.5;
        // Stable clip ID per star position (no random — won't change between renders)
        const clipId = `epocus-star-half-${star}`;

        return (
          <StarButton
            key={star}
            type="button"
            disabled={readOnly}
            aria-label={`${star} star${star > 1 ? "s" : ""}`}
            aria-pressed={
              value >= star - 0.5 && value < star ? "mixed" : value >= star
            }
            onMouseMove={(e) =>
              handleStarInteract(
                star,
                e.clientX,
                e.clientY,
                e.currentTarget.getBoundingClientRect(),
                "move",
              )
            }
            onClick={(e) =>
              handleStarInteract(
                star,
                e.clientX,
                e.clientY,
                e.currentTarget.getBoundingClientRect(),
                "click",
              )
            }
          >
            <StarSvg fill={fill} active={isActive} clipId={clipId} />
          </StarButton>
        );
      })}
    </StarRow>
  );
};
